'use client';

import React, { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { isProfileComplete, resolvePostAuthRedirect } from '@/lib/profile-completion';

const PUBLIC_PATHS = ['/login', '/register', '/auth/callback', '/auth/'];
const ONBOARDING_PATH = '/completar-cadastro';

export default function RouteGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { isAuthenticated, isLoading, user } = useAuth();

  const isLanding = pathname === '/';
  const isPublic = isLanding || PUBLIC_PATHS.some((p) => pathname.startsWith(p));
  const isOnboarding = pathname.startsWith(ONBOARDING_PATH);
  const profileComplete = isProfileComplete(user);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isPublic && !isOnboarding) {
      router.replace(`/login?next=${encodeURIComponent(pathname)}`);
    }
  }, [isAuthenticated, isLoading, isPublic, isOnboarding, pathname, router]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return;

    if (!profileComplete && !isOnboarding) {
      router.replace(ONBOARDING_PATH);
      return;
    }

    if (profileComplete && isOnboarding) {
      void resolvePostAuthRedirect(user).then((path) => router.replace(path));
    }
  }, [isAuthenticated, isLoading, isOnboarding, profileComplete, router, user]);

  if (isPublic || isOnboarding) return <>{children}</>;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated || !profileComplete) return null;

  return <>{children}</>;
}
